import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { forgotPassword, resetPassword, resendResetCode } from '../services/api'
import Logo, { Wordmark } from '../components/Logo'
import Icon from '../components/Icon'
import { useTheme } from '../hooks/useTheme'

const RESEND_COOLDOWN_SECONDS = 60

/**
 * Coque commune aux trois écrans d'authentification : connexion, vérification
 * du code et réinitialisation. Ils partageaient leur mise en page par
 * copier-coller, ce qui les faisait diverger à chaque retouche.
 *
 * Définie au niveau du module, et non dans Login : un composant recréé à
 * chaque rendu change d'identité, React remonte tout son sous-arbre et les
 * champs perdent le focus à chaque frappe.
 */
function AuthShell({ isDark, onToggleTheme, title, subtitle, children, footer }) {
  return (
    <div className="flex min-h-screen flex-col bg-canvas">
      <div className="flex justify-end p-4">
        <button
          type="button"
          onClick={onToggleTheme}
          className="btn-icon"
          aria-label={isDark ? 'Passer en thème clair' : 'Passer en thème sombre'}
        >
          <Icon name={isDark ? 'sun' : 'moon'} size={18} />
        </button>
      </div>

      <div className="flex flex-1 items-start justify-center px-4 pb-16 sm:items-center sm:pb-24">
        <div className="w-full max-w-[26rem]">
          <div className="mb-7 flex flex-col items-center text-center">
            <Logo size={44} />
            <Wordmark className="mt-3 text-2xl text-ink" />
            <h1 className="mt-6 text-2xl font-semibold text-ink">{title}</h1>
            {subtitle && <p className="mt-1.5 text-base text-ink-secondary">{subtitle}</p>}
          </div>

          <div className="card p-6 sm:p-7">{children}</div>

          {footer && <div className="mt-5 text-center">{footer}</div>}
        </div>
      </div>
    </div>
  )
}

function ErrorMessage({ error }) {
  if (!error) return null
  return (
    <div className="callout-negative whitespace-pre-line" role="alert">
      <Icon name="alertCircle" size={18} className="mt-px shrink-0" />
      <span>{error}</span>
    </div>
  )
}

// Saisie d'un code à 6 chiffres : chasse fixe et interlettrage large, pour
// qu'on relise facilement ce qu'on a tapé depuis son e-mail.
const codeInputClass = 'input text-center text-2xl font-semibold tracking-[0.4em] tnum'

export default function Login() {
  const [isLogin, setIsLogin] = useState(true)
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [showVerification, setShowVerification] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  // Mot de passe oublié
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotStep, setForgotStep] = useState('email')
  const [forgotEmail, setForgotEmail] = useState('')
  const [resetCode, setResetCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetResendCooldown, setResetResendCooldown] = useState(0)
  const [resetSuccessMessage, setResetSuccessMessage] = useState('')

  const { login, register, confirmRegistration, resendRegistrationCode } = useAuth()
  const { isDark, toggle: toggleTheme } = useTheme()
  const navigate = useNavigate()

  useEffect(() => {
    if (resendCooldown <= 0) return
    const t = setInterval(() => setResendCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000)
    return () => clearInterval(t)
  }, [resendCooldown])
  useEffect(() => {
    if (resetResendCooldown <= 0) return
    const t = setInterval(() => setResetResendCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000)
    return () => clearInterval(t)
  }, [resetResendCooldown])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      let result
      if (isLogin) {
        result = await login(username, password)
      } else {
        if (!email) {
          setError('L\'email est requis pour l\'inscription')
          setLoading(false)
          return
        }
        result = await register(username, email, password)
      }

      if (result.success) {
        // Inscription : le compte n'est créé qu'après saisie du code (envoyé par email uniquement)
        if (result.requiresVerification) {
          setShowVerification(true)
          setLoading(false)
        } else {
          setTimeout(() => navigate(result.onboardingCompleted ? '/dashboard' : '/', { replace: true }), 100)
        }
      } else {
        // Afficher à l'écran le message d'erreur renvoyé par l'API
        const errorMessage = result.message || 'Une erreur est survenue'
        setError(errorMessage)
        setLoading(false)
      }
    } catch (err) {
      console.error('Erreur lors de l\'inscription (exception):', err)
      // Toujours afficher le message d'erreur à l'écran (API ou proxy/connexion)
      let errorMessage = 'Une erreur est survenue'
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      } else if (err.response?.data?.errors) {
        errorMessage = Object.entries(err.response.data.errors)
          .map(([field, messages]) => {
            const fieldName = field === 'password' ? 'mot de passe' : field
            const msg = Array.isArray(messages) ? messages.join(', ') : messages
            return `${fieldName}: ${msg}`
          })
          .join('\n')
      } else if (
        err.code === 'ECONNRESET' ||
        err.message?.includes('ECONNRESET') ||
        err.message === 'Request failed with status code 500' && !err.response?.data?.message
      ) {
        errorMessage = 'Le serveur a coupé la connexion. Vérifiez que le backend Flask est lancé sur http://localhost:5000 et consultez les logs du terminal où il tourne.'
      } else if (err.response?.status === 503) {
        errorMessage = err.response?.data?.message || "Impossible d'envoyer l'e-mail pour le moment. Pour tester l'application, votre adresse e-mail doit d'abord être approuvée. Vérifiez votre adresse, réessayez dans quelques minutes, ou transmettez-la à admin@financeprevision.com pour demander l'accès."
      } else if (err.response?.status === 502 || (err.response?.status === 500 && !err.response?.data?.message)) {
        errorMessage = 'Backend injoignable ou connexion réinitialisée. Lancez le backend sur http://localhost:5000 et consultez ses logs.'
      } else if (err.response?.status === 500) {
        errorMessage = err.response?.data?.message || 'Erreur serveur (500). Consultez les logs du backend.'
      } else if (err.message) {
        errorMessage = err.message
      }
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (e) => {
    e.preventDefault()
    setError('')
    const normalizedCode = String(verificationCode).trim().replace(/\D/g, '')
    if (normalizedCode.length !== 6 || !/^\d{6}$/.test(normalizedCode)) {
      setError('Le code doit contenir exactement 6 chiffres')
      return
    }
    setVerifying(true)
    try {
      const result = await confirmRegistration(email, normalizedCode)
      if (result.success) {
        setTimeout(() => navigate('/', { replace: true }), 300)
      } else {
        setError(result.message || 'Code invalide ou expiré')
        setVerifying(false)
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Erreur lors de la création du compte'
      setError(errorMessage)
      setVerifying(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCooldown > 0) return
    setError('')
    setResending(true)
    try {
      const result = await resendRegistrationCode(email)
      if (result.success) {
        setError('')
        setResendCooldown(RESEND_COOLDOWN_SECONDS)
      } else {
        setError(result.message || 'Erreur lors du renvoi du code')
      }
    } catch (err) {
      const msg = err.response?.status === 503
        ? (err.response?.data?.message || "Impossible d'envoyer l'e-mail pour le moment. Pour tester l'application, votre adresse e-mail doit d'abord être approuvée. Vérifiez votre adresse, réessayez dans quelques minutes, ou transmettez-la à admin@financeprevision.com pour demander l'accès.")
        : (err.response?.data?.message || err.message || 'Erreur lors du renvoi du code')
      setError(msg)
    } finally {
      setResending(false)
    }
  }

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault()
    setError('')
    if (!forgotEmail.trim()) {
      setError('Saisissez votre adresse email')
      return
    }
    setResetLoading(true)
    try {
      const result = await forgotPassword(forgotEmail.trim())
      if (result.success) {
        setForgotStep('code')
      } else {
        setError(result.message || 'Une erreur est survenue')
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Impossible d'envoyer l'e-mail pour le moment. Pour tester l'application, votre adresse e-mail doit d'abord être approuvée. Vérifiez votre adresse, réessayez dans quelques minutes, ou transmettez-la à admin@financeprevision.com pour demander l'accès.")
    } finally {
      setResetLoading(false)
    }
  }

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault()
    setError('')
    const code = String(resetCode).trim().replace(/\D/g, '').slice(0, 6)
    if (code.length !== 6) {
      setError('Le code doit contenir exactement 6 chiffres')
      return
    }
    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères')
      return
    }
    if (newPassword !== confirmNewPassword) {
      setError('Les deux mots de passe ne correspondent pas')
      return
    }
    setResetLoading(true)
    try {
      const result = await resetPassword({ email: forgotEmail.trim(), code, new_password: newPassword })
      if (result.success) {
        setShowForgotPassword(false)
        setForgotStep('email')
        setForgotEmail('')
        setResetCode('')
        setNewPassword('')
        setConfirmNewPassword('')
        setError('')
        setResetSuccessMessage('Mot de passe réinitialisé. Vous pouvez vous connecter.')
        setTimeout(() => setResetSuccessMessage(''), 5000)
      } else {
        setError(result.message || 'Code invalide ou expiré')
      }
    } catch (err) {
      const msg = err.response?.data?.errors?.new_password
        ? (Array.isArray(err.response.data.errors.new_password) ? err.response.data.errors.new_password.join(', ') : err.response.data.errors.new_password)
        : (err.response?.data?.message || err.message || 'Erreur lors de la réinitialisation')
      setError(msg)
    } finally {
      setResetLoading(false)
    }
  }

  const handleResendResetCode = async () => {
    if (resetResendCooldown > 0) return
    setError('')
    try {
      const result = await resendResetCode(forgotEmail.trim())
      if (result.success) {
        setResetResendCooldown(RESEND_COOLDOWN_SECONDS)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Impossible de renvoyer le code.")
    }
  }

  // Formulaire "Mot de passe oublié"
  if (showForgotPassword) {
    return (
      <AuthShell
        isDark={isDark}
        onToggleTheme={toggleTheme}
        title="Réinitialiser le mot de passe"
        subtitle={
          forgotStep === 'email'
            ? 'Saisissez l’adresse de votre compte pour recevoir un code.'
            : `Un code a été envoyé à ${forgotEmail}.`
        }
        footer={
          <button
            type="button"
            onClick={() => {
              setShowForgotPassword(false)
              setForgotStep('email')
              setForgotEmail('')
              setError('')
            }}
            className="text-base text-ink-secondary transition-colors duration-fast hover:text-ink"
          >
            Retour à la connexion
          </button>
        }
      >
        {forgotStep === 'email' ? (
          <form className="space-y-4" onSubmit={handleForgotPasswordSubmit}>
            <ErrorMessage error={error} />
            <div className="field">
              <label htmlFor="forgot-email" className="field-label">Adresse e-mail du compte</label>
              <input
                id="forgot-email"
                type="email"
                required
                autoFocus
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="input"
                placeholder="vous@exemple.com"
              />
            </div>
            <button type="submit" disabled={resetLoading} className="btn-primary btn-block btn-lg">
              {resetLoading && <Icon name="refresh" size={16} className="animate-spin" />}
              {resetLoading ? 'Envoi…' : 'Envoyer le code'}
            </button>
          </form>
        ) : (
          <form className="space-y-4" onSubmit={handleResetPasswordSubmit}>
            <ErrorMessage error={error} />
            <div className="field">
              <label htmlFor="reset-code" className="field-label">Code reçu par e-mail</label>
              <input
                id="reset-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                autoFocus
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                className={codeInputClass}
                placeholder="000000"
              />
            </div>
            <div className="field">
              <label htmlFor="new-password" className="field-label">Nouveau mot de passe</label>
              <input
                id="new-password"
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
              />
              <p className="field-hint">
                8 caractères minimum, avec une majuscule, une minuscule, un chiffre et un caractère spécial.
              </p>
            </div>
            <div className="field">
              <label htmlFor="confirm-new-password" className="field-label">Confirmer le mot de passe</label>
              <input
                id="confirm-new-password"
                type="password"
                required
                minLength={8}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                className="input"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={resetLoading || resetCode.length !== 6}
              className="btn-primary btn-block btn-lg"
            >
              {resetLoading && <Icon name="refresh" size={16} className="animate-spin" />}
              {resetLoading ? 'Réinitialisation…' : 'Réinitialiser le mot de passe'}
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendResetCode}
                disabled={resetResendCooldown > 0}
                className="text-base font-medium text-accent transition-colors duration-fast hover:text-accent-hover disabled:text-ink-disabled"
              >
                {resetResendCooldown > 0
                  ? `Renvoyer le code (${resetResendCooldown} s)`
                  : 'Renvoyer le code'}
              </button>
            </div>
          </form>
        )}
      </AuthShell>
    )
  }

  // Si on doit afficher le formulaire de vérification
  if (showVerification) {
    return (
      <AuthShell
        isDark={isDark}
        onToggleTheme={toggleTheme}
        title="Confirmez votre adresse"
        subtitle={`Un code à 6 chiffres a été envoyé à ${email}.`}
        footer={
          <button
            type="button"
            onClick={() => {
              setShowVerification(false)
              setVerificationCode('')
              setError('')
            }}
            className="text-base text-ink-secondary transition-colors duration-fast hover:text-ink"
          >
            Retour à la connexion
          </button>
        }
      >
        <form className="space-y-4" onSubmit={handleVerifyCode}>
          <ErrorMessage error={error} />

          <div className="field">
            <label htmlFor="code" className="field-label">Code de vérification</label>
            <input
              id="code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={6}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
              className={codeInputClass}
              placeholder="000000"
              autoFocus
            />
            <p className="field-hint text-center">Valide 30 minutes.</p>
          </div>

          <button
            type="submit"
            disabled={verifying || verificationCode.length !== 6}
            className="btn-primary btn-block btn-lg"
          >
            {verifying && <Icon name="refresh" size={16} className="animate-spin" />}
            {verifying ? 'Vérification…' : 'Créer mon compte'}
          </button>

          <div className="callout-neutral text-sm">
            <Icon name="mail" size={16} className="mt-px shrink-0" />
            <span>L’e-mail peut mettre une à deux minutes à arriver. Pensez aux indésirables.</span>
          </div>

          <div className="text-center">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resending || resendCooldown > 0}
              className="text-base font-medium text-accent transition-colors duration-fast hover:text-accent-hover disabled:text-ink-disabled"
            >
              {resending
                ? 'Envoi…'
                : resendCooldown > 0
                  ? `Renvoyer le code (${resendCooldown} s)`
                  : 'Renvoyer le code'}
            </button>
          </div>
        </form>
      </AuthShell>
    )
  }

  return (
    <AuthShell
        isDark={isDark}
        onToggleTheme={toggleTheme}
      title={isLogin ? 'Connexion' : 'Créer un compte'}
      subtitle={
        isLogin
          ? 'Retrouvez vos comptes et vos prévisions.'
          : 'Quelques secondes suffisent pour commencer.'
      }
      footer={
        <button
          type="button"
          onClick={() => {
            setIsLogin(!isLogin)
            setError('')
          }}
          className="text-base text-ink-secondary transition-colors duration-fast hover:text-ink"
        >
          {isLogin ? (
            <>Pas encore de compte ? <span className="font-medium text-accent">S’inscrire</span></>
          ) : (
            <>Déjà un compte ? <span className="font-medium text-accent">Se connecter</span></>
          )}
        </button>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        {resetSuccessMessage && (
          <div className="callout-positive" role="status">
            <Icon name="checkCircle" size={18} className="mt-px shrink-0" />
            <span>{resetSuccessMessage}</span>
          </div>
        )}
        <ErrorMessage error={error} />

        <div className="field">
          <label htmlFor="username" className="field-label">
            {isLogin ? 'Nom d’utilisateur ou e-mail' : 'Nom d’utilisateur'}
          </label>
          <input
            id="username"
            name="username"
            type="text"
            required
            autoComplete={isLogin ? 'username' : 'nickname'}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="input"
            placeholder={isLogin ? 'vous@exemple.com' : 'Votre nom d’utilisateur'}
          />
        </div>

        {!isLogin && (
          <div className="field">
            <label htmlFor="email" className="field-label">Adresse e-mail</label>
            <input
              id="email"
              name="email"
              type="email"
              required={!isLogin}
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="vous@exemple.com"
            />
          </div>
        )}

        <div className="field">
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="password" className="field-label">Mot de passe</label>
            {isLogin && (
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(true)
                  setError('')
                }}
                className="text-sm font-medium text-accent transition-colors duration-fast hover:text-accent-hover"
              >
                Mot de passe oublié ?
              </button>
            )}
          </div>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              required
              minLength={8}
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input pr-10"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-muted transition-colors duration-fast hover:text-ink"
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
            </button>
          </div>
          {!isLogin && (
            <p className="field-hint">
              8 caractères minimum, avec une majuscule, une minuscule, un chiffre et un caractère spécial.
            </p>
          )}
        </div>

        <button type="submit" disabled={loading} className="btn-primary btn-block btn-lg">
          {loading && <Icon name="refresh" size={16} className="animate-spin" />}
          {loading ? 'Connexion…' : isLogin ? 'Se connecter' : 'Créer mon compte'}
        </button>

        {/* Contrainte d'accès propre à la phase de test : la dire ici évite un
            échec incompréhensible au moment de l'inscription. */}
        <div className="callout-warning text-sm">
          <Icon name="info" size={16} className="mt-px shrink-0" />
          <span>
            Accès en phase de test : votre adresse e-mail doit être approuvée. Pour demander
            l’accès, écrivez à{' '}
            <a
              href="mailto:admin@financeprevision.com"
              className="font-medium underline underline-offset-2"
            >
              admin@financeprevision.com
            </a>
            .
          </span>
        </div>
      </form>
    </AuthShell>
  )
}
